import { newId } from '@/utils.js'
import { db, Timestamp } from '@/firebase.js'
import { firestoreAction } from 'vuexfire'

const getDefaultState = () => {
  return {
    data: [],
    ref: ''
  }
}

const state = getDefaultState()

const getters = {
  data(state) {
    return {
      ...state.data
    }
  },
  ref(state) {
    return state.ref
  },
  products(state) {
    // return object of products where:
    //      hidden == false, or hidden property does not exist
    return Object.assign(
      {},
      ...state.data
        .filter((item) => !item.hidden)
        .map((doc) => ({ [doc.id]: doc }))
    )
  },
  productsNotHidden(state) {
    // return object of products where:
    //      hidden == false, or hidden property does not exist
    return Object.assign(
      {},
      ...state.data
        .filter((item) => !item.hidden)
        .map((doc) => ({ [doc.id]: doc }))
    )
  },
  productsHidden(state) {
    // return object of products where:
    //      hidden property exists, and hidden == true
    return Object.assign(
      {},
      ...state.data
        .filter((item) => !!item.hidden)
        .map((doc) => ({ [doc.id]: doc }))
    )
  },
  productIdsActive(state) {
    // return product ids where:
    //      hidden == false, or hidden property does not exist
    // and  active == true, or active property does not exist
    return state.data
      .filter((item) => !item.hidden && !(item.active == false))
      .map((doc) => doc.id)
  },
  productIdsNotActive(state) {
    // return product ids where:
    //      hidden property exists, and hidden == true
    //  or  active property exists, and active == false
    return state.data
      .filter((item) => !!item.hidden || item.active == false)
      .map((doc) => doc.id)
  },
  productIdsHidden(state) {
    // return product ids where:
    //      hidden property exists, and hidden == true
    return state.data.filter((item) => !!item.hidden).map((doc) => doc.id)
  },
  productIdsNotHidden(state) {
    // return product ids where:
    //      hidden == false, or hidden property does not exist
    return state.data.filter((item) => !item.hidden).map((doc) => doc.id)
  },
  productIdByGenericId(state) {
    // return id => {
    //   return state.data.find(doc => doc.genericProductId === id).id
    return (id) => {
      const product = state.data.find(
        (doc) =>
          doc.genericProductId === id && !doc.hidden && !(doc.active === false)
      )
      return product ? product.id : null
    }
  },
  // genericIdByProductId(state, getters, rootState, rootGetters) {
  //   return id => {
  //     return rootGetters['global/isProduct'](id)
  //       ? id
  //       : state.data.find(doc => doc.id === id).genericProductId
  genericIdByProductId(state) {
    return (id) => {
      const product = state.data.find((doc) => doc.id === id)
      return product ? product.genericProductId : null
    }
  },
  genericIdsByProductIds(state) {
    return Object.assign(
      {},
      ...state.data
        .filter((item) => item.genericProductId)
        .map((doc) => ({ [doc.id]: doc.genericProductId }))
    )
  },
  productIdsByGenericIds(state) {
    return Object.assign(
      {},
      ...state.data
        .filter((item) => item.genericProductId)
        .map((doc) => ({ [doc.genericProductId]: doc.id }))
    )
  },
  productById(state) {
    return (id) => {
      // returns product, regardless of active or hidden value
      return state.data.find((doc) => doc.id === id)
    }
  },
  sortProductIdsByProductName(state, getters) {
    return (ids) => {
      return ids.sort((a, b) => {
        const nameA = getters['productName'](a) || ''
        const nameB = getters['productName'](b) || ''
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase())
      })
    }
  },
  categoryByProductId(state) {
    // returns product category, regardless of active or hidden value
    return (id) => {
      // console.log(state.data.find(doc => doc.id === id).category)
      return state.data.find((doc) => doc.id === id).category
    }
  },
  recipeUnitPerOrderUnitById(state, getters) {
    return (id) => {
      try {
        return getters['productById'](id).recipeUnitPerOrderUnit
      } catch {
        return 1
      }
    }
  },
  qtyPerUnitByProductId(state, getters) {
    return (productId) => {
      try {
        return getters['productById'](productId).qtyPerUnit // change to vendorId
      } catch {
        return null
      }
    }
  },
  productName(state, getters) {
    return (id) => {
      try {
        return getters.products[id].name
      } catch {
        return null
      }
    }
  },
  vendorIdByProductId(state, getters) {
    return (productId) => {
      try {
        return getters['productById'](productId).vendor // change to vendorId
      } catch {
        return null
      }
    }
  },
  isProduct(state, getters) {
    return (id) => {
      try {
        return Object.keys(getters.data).some((k) => getters.data[k].id === id)
      } catch {
        return false
      }
    }
  },
  isHidden(state, getters) {
    return (id) => {
      try {
        if (
          !Object.prototype.hasOwnProperty.call(
            getters.productById(id),
            'hidden'
          )
        )
          return false
        return getters.productById(id).hidden ? true : false
      } catch {
        return false
      }
    }
  },
  isProductActive(state, getters) {
    return (id) => {
      try {
        if (
          !Object.prototype.hasOwnProperty.call(
            getters.productById(id),
            'active'
          )
        )
          return true
        return getters.productById(id).active ? true : false
      } catch {
        return false
      }
    }
  },
  activeProductIdsByGenericIds(state) {
    return Object.assign(
      {},
      ...state.data
        .filter(
          (item) =>
            item.genericProductId && !item.hidden && item.active !== false
        )
        .map((doc) => ({ [doc.genericProductId]: doc.id }))
    )
  },
  productsWithoutGenericId(state) {
    return state.data.filter(
      (item) => !item.genericProductId && !item.hidden && item.active !== false
    )
  },
  isTransferable(state, getters) {
    return (id) => {
      try {
        const product = getters.productById(id)
        // If transferable property doesn't exist or is null, consider it transferable
        if (!Object.prototype.hasOwnProperty.call(product, 'transferable')) {
          return true
        }
        return product.transferable !== false
      } catch {
        return false
      }
    }
  }
}

const actions = {
  bindStore: firestoreAction(
    ({ bindFirestoreRef, commit, state }, { storeId }) => {
      commit(
        'setRef',
        db.collection('stores').doc(storeId).collection('products')
      )
      return bindFirestoreRef('data', state.ref)
    }
  ),
  unbindStore: firestoreAction(({ unbindFirestoreRef }) => {
    unbindFirestoreRef('data')
  }),
  create: async function ({ getters }, payload) {
    let id = newId()
    let data = {
      ...payload,
      transferable:
        payload.transferable !== undefined ? payload.transferable : true, // Default to true if not specified
      createdAt: Timestamp.now(),
      id: id
    }
    console.log('products/create', id, data)
    return getters.ref
      .doc(id)
      .set(data, { merge: true })
      .then(() => id)
      .catch((err) => err)
  },
  delete: async function ({ dispatch }, id) {
    console.log('delete/delete', id)
    if (!id) throw new Error('No ID provided.')
    return dispatch('updateItem', { id, data: { status: 'deleted' } })
  },
  updateItem({ getters, rootGetters }, { id, data }) {
    console.log('products/updateItem', id, data)
    return getters.ref.doc(id).update({
      ...data,
      updatedAt: Timestamp.now(),
      updatedBy: rootGetters['user/id']
    })
  },
  resetStoreState({ commit }) {
    commit('resetState', null)
  }
}

const mutations = {
  setRef(state, ref) {
    state.ref = ref
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  }
}

const products = {
  namespaced: true, // automatically added
  state,
  getters,
  mutations,
  actions
}

export default products
